import type {
  DashboardAgentState,
  DashboardWidget,
  RenderedBoxChildrenInner,
  RenderedDashboard,
} from '../../gen/openapi';
import { RenderedBoxTypeEnum } from '../../gen/openapi';

/** A leaf widget's title lives in its rendered content (charts), not on the node. */
const readContentTitle = (content: unknown): string | null => {
  if (content && typeof content === 'object' && 'title' in content) {
    const title = (content as { title?: unknown }).title;
    if (typeof title === 'string') return title;
  }
  return null;
};

/** Walk the rendered tree, collecting leaf widgets (Box containers carry no id/content). */
const collectWidgets = (node: RenderedBoxChildrenInner | undefined, out: DashboardWidget[]): void => {
  if (!node) return;
  if (node.type === RenderedBoxTypeEnum.Box) {
    for (const child of node.children ?? []) {
      collectWidgets(child, out);
    }
    return;
  }
  out.push({
    id: node.id ?? '',
    type: node.type,
    // Lightweight manifest for the agent's context; the heavy `content` is fetched on demand.
    title: node.title ?? readContentTitle(node.content),
    metricName: node.metricName ?? null,
    content: node.content ?? null,
  });
};

/**
 * Serialize a rendered dashboard into the `DashboardAgentState` handed to the dashboard
 * agent via conversation metadata.
 */
export const toDashboardAgentState = (rendered: RenderedDashboard, timeRange?: string): DashboardAgentState => {
  const widgets: DashboardWidget[] = [];
  collectWidgets(rendered.element, widgets);
  return {
    templateId: rendered.id,
    name: rendered.name,
    timeRange: timeRange ?? rendered.defaultTimeRange,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    widgets: widgets.filter((widget) => widget.id),
  };
};
